import React, { useState, useEffect } from 'react';

const InstallPWAPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [browserType, setBrowserType] = useState('chrome'); // chrome, samsung, ios, other

  useEffect(() => {
    // Already installed as standalone
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Dismissed recently (7 days)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    const ua = navigator.userAgent;

    // Detect browser type
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSamsung = /SamsungBrowser/i.test(ua);
    const isFirefox = /Firefox/i.test(ua) && !/Seamonkey/i.test(ua);
    const isOpera = /OPR|Opera/i.test(ua);
    const isEdge = /Edg/i.test(ua);
    const isChrome = /Chrome/i.test(ua) && !isSamsung && !isOpera && !isEdge;

    if (isIOS) setBrowserType('ios');
    else if (isSamsung) setBrowserType('samsung');
    else if (isFirefox) setBrowserType('firefox');
    else if (isChrome || isEdge) setBrowserType('chrome');
    else setBrowserType('other');

    // For iOS, Samsung, Firefox - show manual guide (no beforeinstallprompt)
    if (isIOS || isSamsung || isFirefox) {
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => clearTimeout(timer);
    }

    // Chrome/Edge - use native install prompt (safe, signed by Google)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
   
  }, []);

  const handleInstall = async () => {
    // Chrome/Edge - use native prompt (no Play Protect issue)
    if (browserType === 'chrome' && deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
      return;
    }
    // All other browsers - show manual guide
    setShowGuide(true);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowGuide(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  // Guide steps based on browser
  const getGuideSteps = () => {
    switch (browserType) {
      case 'ios':
        return {
          title: 'Installa Resta in Pace',
          steps: [
            { text: <>Tocca l'icona <span className="material-symbols-outlined text-sm align-middle text-blue-600">ios_share</span> <strong>Condividi</strong> in basso</> },
            { text: <>Scorri e tocca <strong>"Aggiungi alla schermata Home"</strong></> },
            { text: <>Tocca <strong>"Aggiungi"</strong> in alto a destra</> }
          ]
        };
      case 'samsung':
        return {
          title: 'Installa Resta in Pace',
          steps: [
            { text: <>Tocca l'icona <strong>&#9776;</strong> (menu) in basso a destra</> },
            { text: <>Tocca <strong>"Aggiungi pagina a"</strong></> },
            { text: <>Seleziona <strong>"Schermata Home"</strong></> },
            { text: <>Tocca <strong>"Aggiungi"</strong> per confermare</> }
          ]
        };
      case 'firefox':
        return {
          title: 'Installa Resta in Pace',
          steps: [
            { text: <>Tocca i <strong>3 puntini</strong> in basso a destra</> },
            { text: <>Tocca <strong>"Installa"</strong> o <strong>"Aggiungi alla schermata Home"</strong></> },
            { text: <>Conferma toccando <strong>"Aggiungi"</strong></> }
          ]
        };
      default:
        return {
          title: 'Installa Resta in Pace',
          steps: [
            { text: <>Tocca il <strong>menu del browser</strong> (3 puntini)</> },
            { text: <>Cerca <strong>"Aggiungi alla schermata Home"</strong> o <strong>"Installa app"</strong></> },
            { text: <>Conferma toccando <strong>"Aggiungi"</strong></> }
          ]
        };
    }
  };

  const guide = getGuideSteps();

  return (
    <>
      {/* Install Banner */}
      <div
        className="fixed bottom-[76px] left-3 right-3 z-[1000] md:hidden animate-slide-up"
        data-testid="pwa-install-banner"
      >
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl shadow-xl p-4">
          <div className="flex items-start gap-3">
            <img loading="lazy" decoding="async"
              src="https://static.prod-images.emergentagent.com/jobs/a5f866e2-e980-48dc-baab-bd61582b9a1c/images/664b0963ea78b9ebdb75db1f8ce1af51435c4d4bb40b926db45f6a662822a9d6.png"
              alt="Resta in Pace"
              className="w-12 h-12 rounded-xl shadow-sm flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-sm text-gray-900">Installa Resta in Pace</h4>
              <p className="text-xs text-gray-600 mt-0.5">
                Accedi rapidamente dal tuo telefono. Nessun download necessario.
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
              data-testid="pwa-install-dismiss"
            >
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex-1 py-2.5 bg-amber-600 text-white text-sm font-bold rounded-xl hover:bg-amber-700 transition-colors flex items-center justify-center gap-1.5"
              data-testid="pwa-install-button"
            >
              <span className="material-symbols-outlined text-sm">download</span>
              Installa App
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-100 transition-colors"
            >
              Non ora
            </button>
          </div>
        </div>
      </div>

      {/* Universal Install Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-[1001] bg-black/50 flex items-end md:hidden" onClick={handleDismiss}>
          <div
            className="bg-white w-full rounded-t-3xl p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-5" />
            <div className="flex items-center justify-center gap-3 mb-5">
              <img loading="lazy" decoding="async"
                src="https://static.prod-images.emergentagent.com/jobs/a5f866e2-e980-48dc-baab-bd61582b9a1c/images/664b0963ea78b9ebdb75db1f8ce1af51435c4d4bb40b926db45f6a662822a9d6.png"
                alt="Resta in Pace"
                className="w-10 h-10 rounded-lg"
              />
              <h3 className="text-lg font-bold text-gray-900">{guide.title}</h3>
            </div>
            <div className="space-y-4">
              {guide.steps.map((step, i) => (
                <div key={`item-${i}`} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-700 font-bold text-sm">{i + 1}</span>
                  </div>
                  <p className="text-sm text-gray-700 pt-1.5">{step.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 p-3 bg-green-50 border border-green-200 rounded-xl">
              <p className="text-xs text-green-700 flex items-start gap-2">
                <span className="material-symbols-outlined text-sm mt-0.5">verified</span>
                L'app funziona come un sito web sicuro. Nessun avviso di sicurezza.
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="w-full mt-5 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
            >
              Ho capito
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAPrompt;
